import { useState, useMemo } from 'react';
import {
  AreaChart,
  Area,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import CalcDetail from './CalcDetail';

// Order type configurations
const ORDER_TYPES = {
  all: { label: 'Todos', dataKey: 'ordersTotal', color: '#1976d2', alwaysShow: true },
  cakes: { label: 'Pasteles', dataKey: 'cakesTotal', color: '#c43b21' },
  roscas: { label: 'Roscas', dataKey: 'roscasTotal', color: '#7b1fa2' },
  breads: { label: 'Panes', dataKey: 'breadsTotal', color: '#d4a574' },
  merchant: { label: 'Merchant', dataKey: 'merchantTotal', color: '#0d8742' },
  custom: { label: 'Especiales', dataKey: 'customTotal', color: '#c98700' },
};

/**
 * Calculate total for a dataKey across all chart data
 */
const calculateTotal = (chartData, dataKey) => {
  return chartData.reduce((sum, d) => sum + (d[dataKey] || 0), 0);
};

/**
 * Stock-chart style area chart with order type selector
 */
export function TotalOrdersChart({ chartData, tickInterval = 0, spikeSet = new Set() }) {
  const [selectedType, setSelectedType] = useState('all');

  // Filter order types to only show those with data
  const availableTypes = useMemo(() => {
    if (!chartData || chartData.length === 0) return {};

    return Object.fromEntries(
      Object.entries(ORDER_TYPES).filter(([key, type]) =>
        type.alwaysShow || calculateTotal(chartData, type.dataKey) > 0
      )
    );
  }, [chartData]);

  if (!chartData || chartData.length === 0) {
    return (
      <div className="chart-container stock-chart">
        <h3>Total de Órdenes</h3>
        <div className="empty-state">
          <p>No hay datos para este período</p>
        </div>
      </div>
    );
  }

  // If selected type no longer has data, reset to 'all'
  const activeType = availableTypes[selectedType] ? selectedType : 'all';
  const config = ORDER_TYPES[activeType];
  const { dataKey, color, label } = config;

  // Calculate stats for selected type
  const totalOrders = chartData.reduce((sum, d) => sum + (d[dataKey] || 0), 0);
  const avgPerDay = chartData.length > 0 ? Math.round(totalOrders / chartData.length) : 0;

  // Format full date for tooltip display
  const formatFullDate = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('es-MX', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  // Compute average for spike tooltip
  const chartAvg = chartData.length > 0
    ? chartData.reduce((s, d) => s + (d.ordersTotal || 0), 0) / chartData.length
    : 0;

  // Custom tooltip showing day data based on selected filter
  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload || payload.length === 0) return null;

    const dayData = payload[0]?.payload;
    if (!dayData) return null;

    const value = dayData[dataKey] || 0;
    const isSpike = spikeSet.has(dayData.date);
    const spikeMultiplier = isSpike && chartAvg > 0
      ? Math.round((dayData.ordersTotal / chartAvg) * 10) / 10
      : null;

    // If "all" is selected, show full breakdown
    if (activeType === 'all') {
      const breakdown = [
        { label: 'Pasteles', value: dayData.cakesTotal, color: '#c43b21' },
        { label: 'Roscas', value: dayData.roscasTotal, color: '#7b1fa2' },
        { label: 'Panes', value: dayData.breadsTotal, color: '#d4a574' },
        { label: 'Merchant', value: dayData.merchantTotal, color: '#0d8742' },
        { label: 'Especiales', value: dayData.customTotal, color: '#c98700' },
      ].filter(item => item.value > 0);

      return (
        <div className="detailed-tooltip">
          <p className="tooltip-date">{formatFullDate(dayData.date)}</p>
          {isSpike && (
            <p className="tooltip-spike-badge">{spikeMultiplier}x above average</p>
          )}
          <p className="tooltip-total">
            <span className="tooltip-total-value">{dayData.ordersTotal}</span>
            <span className="tooltip-total-label">órdenes totales</span>
          </p>
          {breakdown.length > 0 && (
            <div className="tooltip-breakdown">
              {breakdown.map((item) => (
                <div key={item.label} className="tooltip-row">
                  <span className="tooltip-dot" style={{ backgroundColor: item.color }} />
                  <span className="tooltip-label">{item.label}</span>
                  <span className="tooltip-value">{item.value}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    // For specific type filter, show only that type
    return (
      <div className="detailed-tooltip">
        <p className="tooltip-date">{formatFullDate(dayData.date)}</p>
        {isSpike && (
          <p className="tooltip-spike-badge">{spikeMultiplier}x above average</p>
        )}
        <p className="tooltip-total">
          <span className="tooltip-total-value" style={{ color }}>{value}</span>
          <span className="tooltip-total-label">{label}</span>
        </p>
      </div>
    );
  };

  // Generate aria label with data summary
  const ariaLabel = `Gráfica de área mostrando ${label.toLowerCase()} en el tiempo. Total: ${totalOrders.toLocaleString()} órdenes, promedio de ${avgPerDay.toLocaleString()} por día.`;

  return (
    <div className="chart-container stock-chart" role="figure" aria-label={ariaLabel}>
      <div className="stock-chart-header">
        <div>
          <h3>{label}</h3>
          <CalcDetail>
            Cada punto = total de ordenes ese dia. Promedio = suma total ÷ dias. Puntos naranjas = dias con 2x+ del promedio del periodo.
          </CalcDetail>
          <div className="stock-chart-stats">
            <span className="stat-total">{totalOrders.toLocaleString()} total</span>
            <span className="stat-avg">
              {avgPerDay.toLocaleString()}/día promedio
            </span>
          </div>
        </div>
        <div className="order-type-selector" role="group" aria-label="Filtrar por tipo de orden">
          {Object.entries(availableTypes).map(([key, type]) => (
            <button
              key={key}
              className={`type-btn ${activeType === key ? 'active' : ''}`}
              onClick={() => setSelectedType(key)}
            >
              {type.label}
            </button>
          ))}
        </div>
      </div>
      <ResponsiveContainer width="100%" height={280}>
        <AreaChart
          data={chartData}
          margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
        >
          <defs>
            <linearGradient id={`gradient-${activeType}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.4} />
              <stop offset="100%" stopColor={color} stopOpacity={0.05} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#e8ebf2" vertical={false} />
          <XAxis
            dataKey="displayDate"
            tick={{ fontSize: 11, fill: '#6c6c7b' }}
            tickLine={false}
            axisLine={{ stroke: '#e8ebf2' }}
            interval={tickInterval}
          />
          <YAxis
            tick={{ fontSize: 11, fill: '#6c6c7b' }}
            tickLine={false}
            axisLine={false}
            allowDecimals={false}
            width={40}
          />
          <Tooltip content={<CustomTooltip />} />
          <Area
            type="monotone"
            dataKey={dataKey}
            stroke={color}
            strokeWidth={2}
            fill={`url(#gradient-${activeType})`}
            dot={(props) => {
              const { cx, cy, payload } = props;
              if (spikeSet.has(payload?.date)) {
                return (
                  <circle
                    key={`spike-${payload.date}`}
                    cx={cx}
                    cy={cy}
                    r={5}
                    fill="#ff5722"
                    stroke="#fff"
                    strokeWidth={2}
                  />
                );
              }
              return null;
            }}
            activeDot={{ r: 5, fill: color, stroke: '#fff', strokeWidth: 2 }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

/**
 * Line chart showing order breakdown by type
 */
export default function OrderTrendsChart({ chartData, tickInterval = 0, title }) {
  // Filter lines to only show those with data
  const lines = useMemo(() => {
    if (!chartData || chartData.length === 0) return [];

    const allLines = [
      { dataKey: 'cakesTotal', name: 'Pasteles', color: '#c43b21' },
      { dataKey: 'roscasTotal', name: 'Roscas', color: '#7b1fa2' },
      { dataKey: 'breadsTotal', name: 'Panes', color: '#d4a574' },
      { dataKey: 'merchantTotal', name: 'Merchant', color: '#0d8742' },
      { dataKey: 'customTotal', name: 'Especiales', color: '#c98700' },
    ];

    return allLines.filter(line => calculateTotal(chartData, line.dataKey) > 0);
  }, [chartData]);

  if (!chartData || chartData.length === 0 || lines.length === 0) {
    return (
      <div className="chart-container">
        <h3>{title}</h3>
        <div className="empty-state">
          <p>No hay datos para este período</p>
        </div>
      </div>
    );
  }

  // Format full date for tooltip display
  const formatFullDate = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('es-MX', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  // Custom tooltip formatter
  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload || payload.length === 0) return null;

    const dayData = payload[0]?.payload;
    if (!dayData) return null;

    return (
      <div className="chart-tooltip">
        <p className="tooltip-date">{formatFullDate(dayData.date)}</p>
        {payload.map((entry, index) => (
          <p key={index} style={{ color: entry.color }}>
            {entry.name}: {entry.value}
          </p>
        ))}
      </div>
    );
  };

  // Generate aria label with data summary
  const lineNames = lines.map((l) => l.name).join(', ');
  const ariaLabel = `Gráfica de líneas mostrando tendencias de órdenes por tipo: ${lineNames}.`;

  return (
    <div className="chart-container" role="figure" aria-label={ariaLabel}>
      <h3>{title}</h3>
      <CalcDetail>
        Cada linea muestra ordenes diarias por tipo de producto.
      </CalcDetail>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart
          data={chartData}
          margin={{ top: 5, right: 10, left: 0, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e8ebf2" vertical={false} />
          <XAxis
            dataKey="displayDate"
            tick={{ fontSize: 11, fill: '#6c6c7b' }}
            tickLine={false}
            axisLine={{ stroke: '#e8ebf2' }}
            interval={tickInterval}
          />
          <YAxis
            tick={{ fontSize: 11, fill: '#6c6c7b' }}
            tickLine={false}
            axisLine={false}
            allowDecimals={false}
            width={40}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend
            iconType="circle"
            iconSize={8}
            wrapperStyle={{ paddingTop: '10px' }}
          />
          {lines.map((line) => (
            <Line
              key={line.dataKey}
              type="monotone"
              dataKey={line.dataKey}
              name={line.name}
              stroke={line.color}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4, fill: line.color, stroke: '#fff', strokeWidth: 2 }}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
